import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Container,
  FormControlLabel,
  Radio,
  RadioGroup
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import Button from "../../Button/Button";
import ExitModalAndSave from "../Modal/ExitModalAndSave";
import { backToRepairAsset, dmcCheckSavedAnswer, isEditState, loadDuplicateRepairPage, resumeDraftRepair, saveIsEligibleTradeForED, setRepairDiagnosticFinalData, userChosenAdditionalDescription, userChosenAdditionalDescriptionCOUNT, userChosenCovidQuestion, userChosenCurrentValue, userChosenDuplicateQuestion, userChosenEligibilityErrorCode, userChosenEmergencyQuestion, userChosenHighLevelDescription, userChosenNotifyAll, userChosenRepairLocation, userChosenRepairLocationId, userChosenResumeRepair, userChosenSaveExit, userChosenSupplementaryQuestions, userChosenVandalismAdditionalInformation, userChosenVandalismQuestion, userChosenVulnerabilityAdditionalInformation, userChosenVulnerabilityQuestion } from "../../ducks/redux/actions/userChoices";
import RepairJourney from "../RepairJourney";
import * as api from "../../api";
import LoadingSpinner from "../../storybook/LoadingSpinner";
import { Helper } from "../../utils/Helper/HelperUtil";
import { selectedRepairReasonDefaultPriority, selectedRepairReasonPriorityOverride } from "../../ducks/redux/actions/dashboardReducer.actions";

interface RepairTradeCategoryProps extends RouteComponentProps<any> { }

const RepairTradeCategory: React.FC<RepairTradeCategoryProps> = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [radioValue, setRadioValue] = useState("");
  const [selectedOptionData, setSelectedOptionData] = useState<any>(null);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [tradeOptions, setTradeOptions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const backValue = useSelector((state: any) => state.userChoicesReducer.backValue);
  const communalAssetwithNoHierarchyVal = useSelector((state: any) => state.userChoicesReducer.communalAssetwithNoHierarchy);
  const isEligibleForEnhancedDiagnostics = useSelector((state: any) => state?.addresses?.isEligibleForEnhancedDiagnostics);
  const originalWorkOrderObject = useSelector((state: any) => state?.userChoicesReducer?.originalWorkOrderData);

  const hasInit = useRef(false);

  const resetReduxFromRepairReasonAndED = useCallback(() => {
    dispatch(userChosenRepairLocation(""));
    dispatch(userChosenRepairLocationId(0));
    dispatch(userChosenHighLevelDescription(""));
    dispatch(userChosenCurrentValue(null));
    dispatch(userChosenNotifyAll(null));
    dispatch(userChosenCovidQuestion(""));
    dispatch(userChosenVulnerabilityAdditionalInformation(""));
    dispatch(userChosenVulnerabilityQuestion("No"));
    dispatch(userChosenEmergencyQuestion(""));
    dispatch(isEditState(false));
    dispatch(userChosenVandalismAdditionalInformation(""));
    dispatch(userChosenVandalismQuestion("No"));
    dispatch(userChosenDuplicateQuestion(""));
    dispatch(userChosenAdditionalDescription(""));
    dispatch(userChosenAdditionalDescriptionCOUNT(0));
    dispatch(userChosenSupplementaryQuestions(""));
    dispatch(dmcCheckSavedAnswer(""));
    dispatch(resumeDraftRepair(false));
    dispatch(userChosenResumeRepair(false));
    dispatch(loadDuplicateRepairPage(false));
    dispatch(selectedRepairReasonPriorityOverride(null));
    dispatch(userChosenEligibilityErrorCode(""));
    dispatch(setRepairDiagnosticFinalData(null));
    dispatch(userChosenSaveExit(false));
    dispatch(selectedRepairReasonDefaultPriority(null));
  }, [dispatch]);

  useEffect(() => {
    const initializeRepairTradePage = async () => {
      if (!isEligibleForEnhancedDiagnostics) {
        props.history.push("/LandingView");
        return;
      }
      if (hasInit.current) return;
      hasInit.current = true;
      resetReduxFromRepairReasonAndED();
      try {
        const data = await api.getReferenceDataRepair("repair-diagnostics-trades");
        const keyValues = data?.[0]?.namespace?.keyValues ?? {};
        const options = Object.values(keyValues);
        setTradeOptions(options);
      } catch (error) {
        console.error("Error fetching repair diagnostics trades:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeRepairTradePage();
  }, [isEligibleForEnhancedDiagnostics, props.history, resetReduxFromRepairReasonAndED]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setRadioValue(value);
    const selected = tradeOptions.find(opt => opt.optionValue === value);
    setSelectedOptionData(selected);
    setButtonDisabled(false);
  };

  const handleContinue = () => {
    const isEDEnabled = selectedOptionData?.isEnhancedDiagnosticsEnabled === "true";
    dispatch(saveIsEligibleTradeForED(isEDEnabled));

    if (isEDEnabled) {
      Helper.removeActiveHousingScript();
      if (window.activeHousingIframeConfig) delete window.activeHousingIframeConfig;

      props.history.push({
        pathname: "/RepairEnhancedDiagnostic",
        state: {
          plentificCategoryId: selectedOptionData?.plentificCategoryId,
        },
      });
    } else {
      props.history.push("/RepairLocation");
    }
  };

  const handleExit = () => {
    dispatch(backToRepairAsset(false));
    props.history.push("/LandingView");
  };

  const handleBack = () => {
    if (!communalAssetwithNoHierarchyVal) {
      dispatch(backToRepairAsset(true));
      props.history.push(Helper.BackRedirectMap[Helper.BackValue.RepairAsset]);
      return;
    }
    props.history.push(Helper.BackRedirectMap[backValue] ?? `/WorkOrder360View/${originalWorkOrderObject?.identifiers?.worksOrderIdentifier}`);
  };


  return (
    <Container id="repairtrade-container" className="ctn-fht">
      <div className="container mx-auto mb-0 md:mb-14 lg:mb-10 px-0 xl:px-0">
        <div className="back-and-journey">
          <p className="text-teal text-xl mt-4 mb-8">
            <button className="back-btn" onClick={handleBack}>
              <i className="fas fa-angle-left mr-2"></i>
              {t("back")}
            </button>
          </p>
          <RepairJourney />
        </div>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="container repair-text">
            <div className="h5 mt-0">
              <p className="text-purple-100 text-2xl mb-2 global-txt">
                {t("repair_trade_category_label")}
              </p>
            </div>

            <div className="mb-12">
              <RadioGroup
                id="repairtrade-radiogroup"
                name="repairtrade-options"
                value={radioValue}
                onChange={handleRadioChange}
                aria-label="Repair Trade Options"
              >
                {tradeOptions.map((option: any, index: number) => (
                  <FormControlLabel
                    key={option.id}
                    value={option.optionValue}
                    control={<Radio id={`repairtrade-radio${index}`} color="primary" />}
                    label={option.optionValue}
                    id={`repairtrade-label${index}`}
                  />
                ))}
              </RadioGroup>
            </div>
          </div>

          <div className="flex justify-between items-center pb-4 md:pb-8 container">
            <ExitModalAndSave
              data-testid="repairtrade-exitmodal"
              exit={handleExit}
              closeAfterSaving={false}
              canSaveAndExit={false}
            />
            <Button
              id="repairtrade-btn-continue"
              disabled={buttonDisabled}
              onClick={handleContinue}
              className="button-class"
            >
              {t("continue")}
              <i className="fas fa-angle-right relativetext-white pointer-events-none arrow"></i>
            </Button>
          </div>
        </>
      )}
    </Container>
  );
};
export default RepairTradeCategory;